import axios from 'axios';
import { ExportRequestDto, ExportResponseDto, ExportJobDto, ExportTypeOption, FulfillmentContextSummary } from '../types/export';
import { Address, CreateAddressRequest, UpdateAddressRequest, AddressExistsResponse } from '../types/addressBook';
import { LabelJobDto } from '../types/label';
import { MappingDocumentRequest, SwaggerOutline } from '../types/mappingDocument';
import { SwaggerFile } from '../types/swaggerFile';
import AuthService from './authService';

// Same as authService: use same-origin when unset so CRA proxy (dev) and server (prod) both work
const API_BASE_URL = process.env.REACT_APP_API_URL || '';

class ApiService {
  private api = axios.create({
    baseURL: API_BASE_URL,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  private authService = AuthService.getInstance();

  constructor() {
    this.setupInterceptors();
  }

  private setupInterceptors(): void {
    // Request interceptor to add auth token
    this.api.interceptors.request.use(
      (config) => {
        const token = this.authService.getToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor to handle token expiration
    this.api.interceptors.response.use(
      (response) => {
        return response;
      },
      (error) => {
        if (error.response?.status === 401) {
          console.log('Unauthorized response received, logging out user');
          this.authService.forceLogout();
        }
        return Promise.reject(error);
      }
    );
  }

  // Export endpoints
  async createExportJob(exportRequest: ExportRequestDto): Promise<ExportResponseDto> {
    const response = await this.api.post('/export', exportRequest);
    return response.data;
  }

  async getAllJobs(): Promise<ExportJobDto[]> {
    const response = await this.api.get('/export/jobs');
    const data = response.data;
    const list = Array.isArray(data) ? data : (data?.data && Array.isArray(data.data) ? data.data : []);
    return list.map((job: any) => ({
      ...job,
      createdAt: new Date(job.createdAt),
      completedAt: job.completedAt ? new Date(job.completedAt) : undefined,
    }));
  }

  async getJobStatus(jobId: string): Promise<ExportJobDto> {
    const response = await this.api.get(`/export/jobs/${jobId}`);
    const job = response.data;
    return {
      ...job,
      createdAt: new Date(job.createdAt),
      completedAt: job.completedAt ? new Date(job.completedAt) : undefined,
    };
  }

  async listFulfillmentContexts(partnerId: string): Promise<FulfillmentContextSummary[]> {
    const response = await this.api.get('/export/fulfillment-contexts', { params: { partnerId } });
    return response.data;
  }

  async getExportTypes(): Promise<{ types: ExportTypeOption[] }> {
    const response = await this.api.get('/export/types');
    return response.data;
  }

  // Legacy partner endpoint
  async legacyExport(username: string, password: string, partnerId?: string): Promise<{ count: number; filenames: string[] }> {
    const response = await this.api.post('/partner/export', {
      username,
      password,
    }, {
      headers: partnerId ? { 'x-delegated-partner-id': partnerId } : {},
    });
    return response.data;
  }

  // Monitoring endpoints
  async getHealthStatus(): Promise<{ status: string; timestamp: string; service: string }> {
    const response = await this.api.get('/monitoring/health');
    return response.data;
  }

  // File download
  async downloadFile(filename: string): Promise<Blob> {
    const response = await this.api.get(`/files/${filename}`, {
      responseType: 'blob',
    });
    return response.data;
  }

  // Download export files as ZIP
  async downloadExportZip(jobId: string): Promise<void> {
    const response = await this.api.get(`/export/jobs/${jobId}/download`, {
      responseType: 'blob',
      validateStatus: (status) => status === 200,
    });
    const blob = new Blob([response.data], { type: response.headers['content-type'] || 'application/zip' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const disposition = response.headers['content-disposition'];
    const filenameMatch = disposition?.match(/filename="?([^";\n]+)"?/);
    link.download = filenameMatch ? filenameMatch[1].trim() : `export-${jobId}.zip`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  // Delete export job
  async deleteExportJob(jobId: string): Promise<void> {
    await this.api.delete(`/export/jobs/${jobId}`);
  }

  // Delete multiple export jobs
  async deleteMultipleExportJobs(jobIds: string[]): Promise<void> {
    await this.api.delete('/export/jobs', { data: { jobIds } });
  }

  // Label endpoints
  async createLabelJob(labelRequest: any): Promise<any> {
    const response = await this.api.post('/label', labelRequest);
    return response.data;
  }

  async getAllLabelJobs(): Promise<LabelJobDto[]> {
    const response = await this.api.get('/label/jobs');
    const data = response.data;
    const list = Array.isArray(data) ? data : (data?.data && Array.isArray(data.data) ? data.data : []);
    return list.map((job: any) => ({
      ...job,
      createdAt: new Date(job.createdAt),
      completedAt: job.completedAt ? new Date(job.completedAt) : undefined,
      totalLabels: job.totalLabels ?? job.total_labels ?? 0,
      successfulLabels: job.successfulLabels ?? job.successful_labels ?? 0,
      failedLabels: job.failedLabels ?? job.failed_labels ?? 0,
      files: job.files ?? [],
    }));
  }

  async getLabelJobStatus(jobId: string): Promise<any> {
    const response = await this.api.get(`/label/jobs/${jobId}`);
    const job = response.data;
    return {
      ...job,
      createdAt: new Date(job.createdAt),
      completedAt: job.completedAt ? new Date(job.completedAt) : undefined,
    };
  }

  async downloadLabels(jobId: string): Promise<void> {
    const response = await this.api.get(`/label/download/${jobId}`, {
      responseType: 'blob',
      validateStatus: (status) => status === 200,
    });
    const blob = new Blob([response.data], { type: response.headers['content-type'] || 'application/zip' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const disposition = response.headers['content-disposition'];
    const filenameMatch = disposition?.match(/filename="?([^";\n]+)"?/);
    link.download = filenameMatch ? filenameMatch[1].trim() : `labels-${jobId}.zip`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  async cancelLabelJob(jobId: string): Promise<{ success: boolean; message: string }> {
    const response = await this.api.post(`/label/jobs/${jobId}/cancel`);
    return response.data;
  }

  async deleteLabelJob(jobId: string): Promise<void> {
    await this.api.delete(`/label/jobs/${jobId}`);
  }

  // Address Book endpoints (normalize to array in case backend wraps or returns non-array)
  async getAllAddresses(): Promise<Address[]> {
    const response = await this.api.get('/address-book');
    const data = response.data;
    return Array.isArray(data) ? data : (data?.data && Array.isArray(data.data) ? data.data : []);
  }

  async getAddressById(id: string): Promise<Address | null> {
    const response = await this.api.get(`/address-book/${id}`);
    return response.data;
  }

  async searchAddresses(query: string): Promise<Address[]> {
    const response = await this.api.get(`/address-book/search?q=${encodeURIComponent(query)}`);
    const data = response.data;
    return Array.isArray(data) ? data : (data?.data && Array.isArray(data.data) ? data.data : []);
  }

  async getAddressesByType(addressType: 'residential' | 'commercial'): Promise<Address[]> {
    const response = await this.api.get(`/address-book/type/${addressType}`);
    const data = response.data;
    return Array.isArray(data) ? data : (data?.data && Array.isArray(data.data) ? data.data : []);
  }

  async createAddress(address: CreateAddressRequest): Promise<Address> {
    const response = await this.api.post('/address-book', address);
    return response.data;
  }

  async updateAddress(id: string, address: UpdateAddressRequest): Promise<Address | null> {
    const response = await this.api.put(`/address-book/${id}`, address);
    return response.data;
  }

  async deleteAddress(id: string): Promise<void> {
    await this.api.delete(`/address-book/${id}`);
  }

  async checkAddressExists(address: CreateAddressRequest): Promise<AddressExistsResponse> {
    const response = await this.api.post('/address-book/check-exists', address);
    return response.data;
  }

  // Mapping Document endpoints
  async createMappingDocumentJob(request: MappingDocumentRequest): Promise<{ jobId: string; message: string; estimatedTime: string }> {
    const response = await this.api.post('/mapping-document/generate', request);
    return response.data;
  }

  async getMappingDocumentJobs(): Promise<any[]> {
    const response = await this.api.get('/mapping-document/jobs');
    const data = response.data;
    return Array.isArray(data) ? data : (data?.data && Array.isArray(data.data) ? data.data : []);
  }

  async getMappingDocumentJobStatus(jobId: string): Promise<any> {
    const response = await this.api.get(`/mapping-document/jobs/${jobId}`);
    return response.data;
  }

  async deleteMappingDocumentJob(jobId: string): Promise<{ success: boolean }> {
    const response = await this.api.delete(`/mapping-document/jobs/${jobId}`);
    return response.data;
  }

  async downloadMappingDocumentJob(jobId: string): Promise<void> {
    const response = await this.api.get(`/mapping-document/jobs/${jobId}/download`, {
      responseType: 'blob',
      validateStatus: (status) => status === 200,
    });
    const contentDisposition = response.headers['content-disposition'];
    let filename = `mapping-document-${jobId}.xlsx`;
    const filenameMatch = contentDisposition?.match(/filename="?([^";\n]+)"?/);
    if (filenameMatch) {
      filename = filenameMatch[1].trim();
    }
    const blob = new Blob([response.data], {
      type: response.headers['content-type'] || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  // Swagger Files endpoints
  async uploadSwaggerFile(file: File, description?: string, tags?: string[]): Promise<any> {
    const formData = new FormData();
    formData.append('file', file);
    if (description) formData.append('description', description);
    if (tags && tags.length > 0) formData.append('tags', tags.join(','));

    const response = await this.api.post('/swagger-files/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  async getAllSwaggerFiles(): Promise<SwaggerFile[]> {
    const response = await this.api.get('/swagger-files');
    const data = response.data;
    return Array.isArray(data) ? data : (data?.data && Array.isArray(data.data) ? data.data : []);
  }

  async getSwaggerFile(id: string): Promise<any> {
    const response = await this.api.get(`/swagger-files/${id}`);
    return response.data;
  }

  async getSwaggerFileContent(id: string): Promise<any> {
    const response = await this.api.get(`/swagger-files/${id}/content`);
    return response.data;
  }

  /** Save a swagger file's spec locally. `/content` returns the parsed spec, so whitespace may differ from the original upload. */
  async downloadSwaggerFile(id: string, filename: string): Promise<void> {
    const response = await this.api.get(`/swagger-files/${id}/content`, { responseType: 'blob' });
    const blob = new Blob([response.data], { type: 'application/json' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  /** Get parsed tags, operations and spec info from a swagger file (for Mapping Document page). */
  async getSwaggerTagsAndOperations(id: string): Promise<SwaggerOutline> {
    const response = await this.api.get(`/swagger-files/${id}/tags-and-operations`);
    let data = response.data;
    if (typeof data === 'string') {
      try {
        data = JSON.parse(data);
      } catch {
        data = null;
      }
    }
    if (!data || typeof data !== 'object') data = {};
    const info = data.info && typeof data.info === 'object' ? data.info : {};
    return {
      tags: Array.isArray(data.tags) ? data.tags : [],
      operationIds: Array.isArray(data.operationIds) ? data.operationIds : [],
      operations: Array.isArray(data.operations) ? data.operations : [],
      info: {
        title: typeof info.title === 'string' ? info.title : '',
        version: typeof info.version === 'string' ? info.version : '',
        specVersion: typeof info.specVersion === 'string' ? info.specVersion : '',
        schemaCount: typeof info.schemaCount === 'number' ? info.schemaCount : 0,
      },
    };
  }

  async updateSwaggerFile(id: string, updates: { description?: string; tags?: string[] }): Promise<any> {
    const response = await this.api.put(`/swagger-files/${id}`, updates);
    return response.data;
  }

  async deleteSwaggerFile(id: string): Promise<void> {
    await this.api.delete(`/swagger-files/${id}`);
  }
}

export const apiService = new ApiService();
